import { useIO } from "@linked/react";
import { styled } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useLinked } from '@type-r/react';
import { CollapsableSection, Column, Columns, div, FormSelect, Input, Loading, Option } from 'lqx-react';
import * as React from 'react';
import NumberFormat from 'react-number-format';
import { hasRole } from 'src/app/services/can';
import { Company, getCompanies, getCompaniesAsCommonParticipants } from '../../api/company';
import { ManualPayment } from '../../api/manualPayment';
import { Payment } from '../../api/payment';
import { PaymentMethods, PaymentTypes } from '../../api/reference-data';
import { DigiCurrencySelect, DigiSelect } from '../../components/select';
import { DigiSelectAsync } from '../../components/select-async';
import { asTimelessLinkedDate, DigiDateInput, ErrorHighligter } from '../../shared';
import { useMemo } from "react";
import { globalStore } from 'api/store';

export const PaymentForm = ({ payment }: { payment: Payment | ManualPayment }) => {
    const classes = useStyles();
    const isReady = useIO(
        async () => {
            if (payment.dataHolderId) {
                $dataHolder.set((await getCompanies(null, payment.dataHolderId)).items[0]);
            }
        });

    const $dataHolder = useLinked<Company>(new Company())
        .onChange(s => {
            if (s && s.id) {
                payment.dataHolderId = Number(s.id);
            }
        });

    const issues = useMemo(() => payment.resolveIssues(), [payment['_changeToken']]);

    return isReady ? <>
        {hasRole('Administrator') &&
            <Columns>
                <PaymentColumn>
                    <PaymentLabel>Data Owner</PaymentLabel>
                    <DigiSelectAsync
                        data-qa-id={`data owner`}
                        getItems={getCompanies}
                        $value={$dataHolder}
                    />
                </PaymentColumn>
            </Columns>
        }
        <Columns>
            <PaymentColumn>
                <PaymentLabel>Payor *</PaymentLabel>
                <ErrorHighligter error={issues['debtorBankAccount'] || issues['debtorBankAccount.Payor.Name']}>
                    <DigiSelectAsync
                        data-qa-id={"payor"}
                        getItems={getCompaniesAsCommonParticipants}
                        $value={payment.debtorBankAccount.$.payor}
                    />
                </ErrorHighligter>
            </PaymentColumn>
        </Columns>
        <Columns>
            <BankAccountColumn>
                <BankAccount>
                    <CollapsableSection title="Bank Account Information  " noMargins>
                        <Columns>
                            <PaymentColumn>
                                <PaymentLabel>Name</PaymentLabel>
                                <ErrorHighligter error={issues['debtorBankAccount.Name']}>
                                    <Input $value={payment.debtorBankAccount.$.name} aria-label="Name" />
                                </ErrorHighligter>
                            </PaymentColumn>
                            <PaymentColumn>
                                <PaymentLabel>Alias</PaymentLabel>
                                <ErrorHighligter error={issues['debtorBankAccount.Alias']}>
                                    <Input $value={payment.debtorBankAccount.$.alias} aria-label="Alias" />
                                </ErrorHighligter>
                            </PaymentColumn>
                            <PaymentColumn>
                                <PaymentLabel>Account Number</PaymentLabel>
                                <ErrorHighligter error={issues['debtorBankAccount.Number']}>
                                    <Input $value={payment.debtorBankAccount.$.number} aria-label="Account Number" />
                                </ErrorHighligter>
                            </PaymentColumn>
                            <PaymentColumn>
                                <PaymentLabel>Address</PaymentLabel>
                                <ErrorHighligter error={issues['debtorBankAccount.Address']}>
                                    <Input $value={payment.debtorBankAccount.$.address} aria-label="Account Address" />
                                </ErrorHighligter>
                            </PaymentColumn>
                        </Columns>
                    </CollapsableSection>
                </BankAccount>
            </BankAccountColumn>
        </Columns>
        <Columns>
            <PaymentColumn>
                <PaymentLabel>Payee *</PaymentLabel>
                <ErrorHighligter error={issues['creditorBankAccount'] || issues['creditorBankAccount.Payee.Name']}>
                    <DigiSelectAsync
                        data-qa-id={"payee"}
                        getItems={getCompaniesAsCommonParticipants}
                        $value={payment.creditorBankAccount.$.payee}
                    />
                </ErrorHighligter>
            </PaymentColumn>
        </Columns>
        <Columns>
            <BankAccountColumn>
                <BankAccount>
                    <CollapsableSection title="Bank Account Information  " noMargins>
                        <Columns>
                            <PaymentColumn>
                                <PaymentLabel>Name</PaymentLabel>
                                <ErrorHighligter error={issues['creditorBankAccount.Name']}>
                                    <Input $value={payment.creditorBankAccount.$.name} aria-label="Name"/>
                                </ErrorHighligter>
                            </PaymentColumn>
                            <PaymentColumn>
                                <PaymentLabel>Alias</PaymentLabel>
                                <ErrorHighligter error={issues['creditorBankAccount.Alias']}>
                                    <Input $value={payment.creditorBankAccount.$.alias} aria-label="Alias" />
                                </ErrorHighligter>
                            </PaymentColumn>
                            <PaymentColumn>
                                <PaymentLabel>Account Number</PaymentLabel>
                                <ErrorHighligter error={issues['creditorBankAccount.Number']}>
                                    <Input $value={payment.creditorBankAccount.$.number} aria-label="Account Number" />
                                </ErrorHighligter>
                            </PaymentColumn>
                            <PaymentColumn>
                                <PaymentLabel>Address</PaymentLabel>
                                <ErrorHighligter error={issues['creditorBankAccount.Address']}>
                                    <Input $value={payment.creditorBankAccount.$.address} aria-label="Address"/>
                                </ErrorHighligter>
                            </PaymentColumn>
                        </Columns>
                    </CollapsableSection>
                </BankAccount>
            </BankAccountColumn>
        </Columns>
        <Columns>
            <PaymentColumn>
                <PaymentLabel>Payment Type *</PaymentLabel>
                <ErrorHighligter error={issues['paymentType']}>
                    <DigiSelect data-qa-id="payment type" id="id" label="name" $value={payment.$.paymentType} options={PaymentTypes} aria-label="Payment Type" />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Reference Number *</PaymentLabel>
                <ErrorHighligter error={issues['referenceNumber']}>
                    <Input data-qa-id="reference number" $value={payment.$.referenceNumber} aria-label="Reference Number" />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Payment Amount *</PaymentLabel>
                <ErrorHighligter error={issues['paymentAmount']}>
                    <NumberFormat value={payment.paymentAmount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; payment.$.paymentAmount.set(floatValue) }} aria-label="Payment Amount" />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Currency *</PaymentLabel>
                <ErrorHighligter error={issues['currency']}>
                    <DigiCurrencySelect
                        aria-label="Currency"
                        isMulti={false}
                        options={globalStore.currenciesLookup.items.models}
                        $value={payment.$.currency}
                    />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Value Date *</PaymentLabel>
                <ErrorHighligterStyled error={issues['assetPaymentDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput date $value={asTimelessLinkedDate(payment.$.assetPaymentDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Original Asset Obligor</PaymentLabel>
                <ErrorHighligter error={issues['originalAssetObligor'] || issues['originalAssetObligor.Name']}>
                    <DigiSelectAsync
                        data-qa-id={"originalAssetObligor"}
                        getItems={getCompaniesAsCommonParticipants}
                        $value={payment.$.originalAssetObligor}
                    />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Payment Details</PaymentLabel>
                <ErrorHighligter error={issues['paymentDetails']}>
                    <Input data-qa-id="payment details" $value={payment.$.paymentDetails} aria-label="Payment Details" />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Lock Box Number</PaymentLabel>
                <ErrorHighligter error={issues['lockBoxNumber']}>
                    <Input data-qa-id="lock box number" $value={payment.$.lockBoxNumber} aria-label="Lock Box Number" />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Payment Method</PaymentLabel>
                <ErrorHighligter error={issues['paymentMethod']}>
                    <DigiSelect data-qa-id="payment method" id="id" label="name" $value={payment.$.paymentMethod} options={PaymentMethods} aria-label="Payment Method" />
                </ErrorHighligter>
            </PaymentColumn>
            <PaymentColumn>
                <PaymentLabel>Bank Entry Date</PaymentLabel>
                <ErrorHighligterStyled error={issues['bankEntryDate']}>
                    <DigiDateWrapper>
                        <DigiDateInput date $value={asTimelessLinkedDate(payment.$.bankEntryDate)} />
                    </DigiDateWrapper>
                </ErrorHighligterStyled>
            </PaymentColumn>
        </Columns>
    </> : <Loading />;
};

const useStyles = makeStyles({
    numericInput: {
        color: '#000',
        width: '100%',
        border: '1px solid #E3E9ED',
        padding: '4px 3px 3px 3px',
        fontSize: '14px',
        boxSizing: 'border-box',
        fontFamily: 'Montserrat',
        fontWeight: 500,
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 2,
        backgroundColor: '#FFF',
        textAlign: 'end',

        '&:focus': {
            backgroundColor: '#F6FCFF',
            border: '1px solid #1084D3',
            outline: 'none',
        },

        '&:disabled': {
            backgroundColor: 'rgba(227,233,237,0.25)',
            border: '1px solid #E3E9ED',
        },

        '&:read-only': {
            border: 'none'
        },

        '&:hover:read-write': {
            border: '1px solid #1084D3',
        }
    }
});

const PaymentColumn = styled(Column)({
    minWidth: '200px'
});

const BankAccountColumn = styled(Column)({
    margin: 0
});

const CompanySelect = styled(DigiSelect)({
    width: '50px'
});

const PaymentLabel = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '15px',
    color: '#8695A4',
    padding: '5px'
});

const BankAccount = div({
    marginTop: '15px',
    marginBottom: '15px'
});

const DigiDateWrapper = styled('div')({
    '& .react-datepicker-wrapper': {
        width: '100%',
    },
    '& input': {
        minWidth: '200px'
    }
})

const ErrorHighligterStyled = styled(ErrorHighligter)({
    boxSizing: 'border-box',
    width: '100%'
})

